import React from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { UnifiedChartData } from "./types";
import { formatNumber } from "../../../api/DataTypeUtils";
import { colors } from "../../../board/components/TaskDetail/ColorPicker";

type Props = {
  data: UnifiedChartData;
  pieColors?: string[];
  height?: number;
};

const DonutChart = ({ data, pieColors = colors, height = 440 }: Props) => {
  const series = data.series[0] || { name: "Series", data: [] };
  const rows = data.labels.map((label, i) => ({
    name: label,
    value: series.data[i] ?? 0,
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="recharts-default-tooltip px-3 py-2 rounded-md bg-[#111827]">
          {payload.map((item, index) => (
            <React.Fragment key={`item-${index}`}>
              <p className="text-[#D1D5DB]">{item?.name}</p>
              <p key={`p-${index}`} className="text-[#ffffff]">
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ backgroundColor: item?.payload?.fill || colors[0] }}
                ></span>
                <span className="ml-2">{formatNumber(item?.value || 0)}</span>
              </p>
            </React.Fragment>
          ))}
        </div>
      );
    }
    return null;
  };

  const CustomLegend = () => {
    const total = rows.reduce((sum, d) => sum + d.value, 0);
    return (
      <div className="flex flex-col items-center">
        <div>
          <span className="font-semibold text-gray-500 mr-2">Total value is</span>
          <span className="font-medium text-gray-700">{formatNumber(total)}</span>
        </div>
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie dataKey="value" data={rows} innerRadius={70} outerRadius={100} paddingAngle={2}>
          {rows.map((_, idx) => (
            <Cell
              key={idx}
              stroke="#fff"
              strokeWidth={1}
              fill={pieColors[idx % pieColors.length]}
            />
          ))}
        </Pie>
        <Tooltip content={CustomTooltip} />
        <Legend content={CustomLegend} />
      </PieChart>
    </ResponsiveContainer>
  );
};

export default DonutChart;
